import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, QrCode, ChevronDown, Plus, MapPin, Home, Users, Box, Bell } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardHeader = ({ 
  yards = [], 
  hives = [],
  selectedYard, 
  onYardChange, 
  onScanClick,
  onAddApiary,
  onAddHive,
  onAddBeekeeper,
  companyProfile,
  appSettings
}) => {
  const navigate = useNavigate();
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);

  // Poll notification count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        const res = await axios.get(`${API}/notifications/diy/count`, { headers: { Authorization: `Bearer ${token}` } });
        setNotifCount(res.data?.count || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const openNotifs = async () => {
    setShowNotifs(!showNotifs);
    if (!showNotifs) {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/notifications/diy`, { headers: { Authorization: `Bearer ${token}` } });
        setNotifs(res.data || []);
      } catch { /* ignore */ }
    }
  };

  const markAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/notifications/diy/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifCount(0);
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch { /* ignore */ }
  };

  // Search functionality
  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      performSearch();
    }
  };

  const performSearch = () => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return;

    // Search for hive by number
    const hiveMatch = hives.find(h => 
      h.hive_number?.toString() === query ||
      h.hive_number?.toString().includes(query) ||
      h.nickname?.toLowerCase().includes(query)
    );
    
    if (hiveMatch) {
      navigate(`/?view=hive-detail&hiveId=${hiveMatch.id}`);
      setSearchQuery('');
      setShowSearchResults(false);
      return;
    }

    // Search for yard
    const yardMatch = yards.find(y => y.name.toLowerCase().includes(query));
    if (yardMatch) {
      onYardChange(yardMatch.name);
      navigate('/?view=hives');
      setSearchQuery('');
      setShowSearchResults(false);
    }
  };

  // Get search suggestions
  const getSearchSuggestions = () => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    
    const suggestions = [];
    
    // Match hives
    hives.forEach(h => {
      if (h.hive_number?.toString().includes(query) || h.nickname?.toLowerCase().includes(query)) {
        suggestions.push({ type: 'hive', item: h, label: `Hive ${h.hive_number}${h.nickname ? ` "${h.nickname}"` : ''}` });
      }
    });
    
    // Match yards
    yards.forEach(y => {
      if (y.name.toLowerCase().includes(query)) {
        suggestions.push({ type: 'yard', item: y, label: `${y.name} (Apiary)` });
      }
    });
    
    return suggestions.slice(0, 5);
  };

  const suggestions = getSearchSuggestions();

  return (
    <div 
      className="h-20 bg-cover bg-center border-b border-stone-200 flex items-center px-4 gap-4"
      style={{
        backgroundImage: companyProfile?.background_image
          ? `linear-gradient(to right, rgba(255,255,255,0.82), rgba(255,255,255,0.65)), url('${process.env.REACT_APP_BACKEND_URL}/api/uploads/${companyProfile.background_image}')`
          : "linear-gradient(to right, rgba(255,255,255,0.82), rgba(255,255,255,0.65)), url('https://images.unsplash.com/photo-1623018697148-8350cf18e64e?w=1200&q=80')",
      }}
    >
      {/* App Logo/Branding — User's business identity on the banner */}
      <div 
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/50 rounded-lg transition-colors"
        onClick={() => navigate('/')}
        data-testid="header-logo"
      >
        {companyProfile?.logo ? (
          <img 
            src={`${BACKEND_URL}/api/uploads/${companyProfile.logo}`}
            alt="Logo"
            className="w-10 h-10 rounded-lg object-contain bg-white/80 p-0.5 shadow-sm border border-white/50"
          />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shadow-sm border border-amber-200">
            <span className="text-xl">🐝</span>
          </div>
        )}
        <div>
          <span className="font-bold text-stone-800 text-sm leading-tight block">
            {companyProfile?.company_name || 'My Apiary'}
          </span>
          {companyProfile?.tagline && (
            <span className="text-[10px] text-stone-500 leading-tight block">{companyProfile.tagline}</span>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex-1 max-w-md relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            type="text"
            placeholder="Search hives, apiaries..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchResults(true); }}
            onKeyDown={handleSearch}
            onFocus={() => setShowSearchResults(true)}
            onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
            className="w-full pl-10 pr-10 py-2 bg-white border border-stone-200 rounded-lg text-sm text-black placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
            data-testid="dashboard-search"
          />
          <button
            onClick={performSearch}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded"
            title="Search"
          >
            <Search className="h-4 w-4 text-amber-600" />
          </button>
        </div>
        
        {/* Search Suggestions */}
        {showSearchResults && suggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-50">
            {suggestions.map((s, idx) => (
              <button
                key={idx}
                onClick={() => {
                  if (s.type === 'hive') {
                    navigate(`/?view=hive-detail&hiveId=${s.item.id}`);
                  } else {
                    onYardChange(s.item.name);
                    navigate('/?view=hives');
                  }
                  setSearchQuery('');
                  setShowSearchResults(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 text-black flex items-center gap-2"
              >
                <span>{s.type === 'hive' ? '🐝' : '📍'}</span>
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add Button */}
      <div className="relative">
        <Button
          onClick={() => setShowAddDropdown(!showAddDropdown)}
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold h-9 px-4"
          data-testid="header-add-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
          <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showAddDropdown ? 'rotate-180' : ''}`} />
        </Button>
        
        {showAddDropdown && (
          <div className="absolute top-full right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-50 min-w-[180px]">
            <button
              onClick={() => { navigate('/crew-management'); setShowAddDropdown(false); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 text-black flex items-center gap-2"
            >
              <Users className="h-4 w-4 text-purple-600" />
              Manage Crew
            </button>
            <button
              onClick={() => { onAddApiary?.(); setShowAddDropdown(false); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 text-black flex items-center gap-2"
            >
              <MapPin className="h-4 w-4 text-sky-600" />
              Add Apiary
            </button>
            <button
              onClick={() => { onAddHive?.(); setShowAddDropdown(false); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 text-black flex items-center gap-2"
            >
              <Home className="h-4 w-4 text-amber-600" />
              Add Hive
            </button>
            <button
              onClick={() => { navigate('/swarm-traps'); setShowAddDropdown(false); }}
              className="w-full px-4 py-2 text-left text-sm hover:bg-stone-50 text-black flex items-center gap-2"
            >
              <Box className="h-4 w-4 text-orange-600" />
              Swarm Traps
            </button>
          </div>
        )}
      </div>

      {/* Notification Bell */}
      <div className="relative">
        <button
          onClick={openNotifs}
          className="relative p-2 rounded-lg hover:bg-white/60 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className={`h-5 w-5 ${notifCount > 0 ? 'text-amber-600' : 'text-stone-500'}`} />
          {notifCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 animate-pulse">
              {notifCount > 9 ? '9+' : notifCount}
            </span>
          )}
        </button>
        {showNotifs && (
          <div className="absolute top-full right-0 mt-1 bg-white border border-stone-200 rounded-xl shadow-lg z-50 w-80 max-h-96 overflow-y-auto" data-testid="notification-dropdown">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
              <span className="font-semibold text-sm text-stone-800">Notifications</span>
              {notifCount > 0 && (
                <button onClick={markAllRead} className="text-xs text-amber-600 hover:underline font-medium">Mark all read</button>
              )}
            </div>
            {notifs.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-stone-400">No notifications yet</div>
            ) : notifs.map(n => (
              <button
                key={n.id}
                onClick={() => { setShowNotifs(false); if (n.idea_id) navigate(`/diy-ideas/${n.idea_id}`); }}
                className={`w-full px-4 py-3 text-left border-b border-stone-50 hover:bg-stone-50 transition ${!n.read ? 'bg-amber-50/50' : ''}`}
              >
                <div className="text-sm text-stone-700">
                  <span className="font-semibold text-amber-700">{n.builder_name}</span>
                  {n.type === 'new_idea' && ' shared a new idea'}
                  {n.type === 'challenge_update' && ' has a challenge update'}
                </div>
                {n.idea_title && <div className="text-xs text-stone-500 mt-0.5 truncate">{n.idea_title}</div>}
                <div className="text-[10px] text-stone-400 mt-1">{(n.created_at || '').split('T')[0]}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Scan Button */}
      <Button
        onClick={onScanClick}
        variant="outline"
        className="border-stone-300 text-black font-semibold h-9 px-4"
        data-testid="header-scan-btn"
      >
        <QrCode className="h-4 w-4 mr-2" />
        Scan
      </Button>

      {/* Pollinate & Prosper — hidden per user request (paused for rethink) */}
      {/*<button
        onClick={() => navigate('/referral-rewards')}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-amber-50 transition-colors border border-amber-200/60 bg-white/80"
        data-testid="header-pollinate-prosper"
        title="Pollinate & Prosper"
      >
        <img src="/pollinate-prosper-logo.png" alt="" className="h-8 w-8 rounded-full object-cover" />
        <div className="hidden lg:flex flex-col items-start leading-none">
          <span className="text-xs font-bold text-amber-700">Pollinate</span>
          <span className="text-xs font-bold text-amber-700">& Prosper <span className="text-amber-500">$</span></span>
        </div>
      </button>*/}
    </div>
  );
};

export default DashboardHeader;
